import type { LDClient } from 'launchdarkly-js-client-sdk';

let client: LDClient | undefined;

export function setLdClient(c: LDClient) {
  client = c;
}

let lastIdentify: { client: LDClient; email: string; done: Promise<unknown> } | undefined;

/**
 * Identifies the user by email, once per email: the early boot identifies from the access token's email
 * claim, and the boot's own identify then waits for that one instead of repeating it.
 */
export function identifyLdUser(ldClient: LDClient, email: string): Promise<unknown> {
  if (lastIdentify?.client === ldClient && lastIdentify.email === email) {
    return lastIdentify.done;
  }
  const done = ldClient.identify({ kind: 'user', key: email, email });
  lastIdentify = { client: ldClient, email, done };
  // A failed identify is not shared: the next call makes its own.
  done.catch(() => {
    if (lastIdentify?.done === done) {
      lastIdentify = undefined;
    }
  });
  return done;
}

export function __TEST_ONLY__forgetLdIdentify() {
  lastIdentify = undefined;
}

/**
 * Access LD flag values outside React components (e.g., in service functions).
 *
 * IMPORTANT: This relies on the LD client being initialized before any call.
 * AppLoader gates on useInitLaunchDarkly() completing before rendering children,
 * so this is safe for any code that runs after app initialization.
 * If you restructure the initialization order, ensure setLdClient is called
 * before any service that uses getLdFlag.
 *
 * TODO: Remove this entire file once useNewViewsP AND showFieldsManager are
 * enabled for all clients. When that happens, also remove:
 *  - setLdClient call in useInitLaunchDarkly (src/hooks/useInitApp.ts)
 *  - getLdFlag calls in fetchViews/fetchColumns (src/services/queries/MaggieViewQueries.ts)
 */
export function getLdFlag<T>(flag: string, defaultValue: T): T {
  return client?.variation(flag, defaultValue) ?? defaultValue;
}
